// src/pages/AIMatchmakingPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAIWorkflow } from "../contexts/AIWorkflowContext";
import { useAuth } from "../contexts/AuthContext";
import ProfileInput from "../components/dashboard/ai_flow/ProfileInput";
import JobCategorySelector from "../components/dashboard/ai_flow/JobCategorySelector";
import "../components/dashboard/ai_flow/aiFlowStyles.css";
import "../components/dashboard/widgets/Widgets.css";
import "../styles/aiMatchmakingMobile.css";

const canadianProvincesAndTerritories = [
  { name: "All of Canada", value: "Canada" },
  { name: "Alberta", value: "Alberta, Canada" },
  { name: "British Columbia", value: "British Columbia, Canada" },
  { name: "Manitoba", value: "Manitoba, Canada" },
  { name: "New Brunswick", value: "New Brunswick, Canada" },
  {
    name: "Newfoundland and Labrador",
    value: "Newfoundland and Labrador, Canada",
  },
  { name: "Nova Scotia", value: "Nova Scotia, Canada" },
  { name: "Ontario", value: "Ontario, Canada" },
  { name: "Prince Edward Island", value: "Prince Edward Island, Canada" },
  { name: "Quebec", value: "Quebec, Canada" },
  { name: "Saskatchewan", value: "Saskatchewan, Canada" },
  { name: "Northwest Territories", value: "Northwest Territories, Canada" },
  { name: "Nunavut", value: "Nunavut, Canada" },
  { name: "Yukon", value: "Yukon, Canada" },
];
const DEFAULT_LOCATION_VALUE = canadianProvincesAndTerritories[0].value;

function AIMatchmakingPage() {
  const navigate = useNavigate();
  const {
    startAnalysisAndGetCategories,
    generateMasterResumeAction,
    resetWorkflow,
    setSelectedCategory,
    isLoadingCategories,
    categoryError,
    userLocationInput: contextLocation,
    jobCategories,
    selectedCategory,
    isLoadingMasterResume,
    masterResumeError,
    aiMatchedJobs,
    isLoadingAiJobs,
    aiJobsError,
    aiTaskId,
    pollingForJobs,
    flowShouldSkipResumeBuilder,
    // Add this new function from context - you'll need to implement it
    searchJobsForCategory,
  } = useAIWorkflow();

  const { currentUser } = useAuth();

  const [localLocation, setLocalLocation] = useState(() => {
    if (
      contextLocation &&
      canadianProvincesAndTerritories.some((p) => p.value === contextLocation)
    ) {
      return contextLocation;
    }
    return DEFAULT_LOCATION_VALUE;
  });

  // Add state to track if job search has been triggered
  const [hasSearchedJobs, setHasSearchedJobs] = useState(false);

  useEffect(() => {
    const newLocationFromContext =
      contextLocation &&
      canadianProvincesAndTerritories.some((p) => p.value === contextLocation)
        ? contextLocation
        : DEFAULT_LOCATION_VALUE;

    setLocalLocation((currentLocalLocation) => {
      if (newLocationFromContext !== currentLocalLocation) {
        console.log(
          `[AIMatchmakingPage] Syncing localLocation from context. Context: ${contextLocation}, Current local was: ${currentLocalLocation}. Setting local to: ${newLocationFromContext}`
        );
        return newLocationFromContext;
      }
      return currentLocalLocation;
    });
  }, [contextLocation]);

  const handleAnalyze = useCallback(
    async (textToSubmit, locationToSubmit, sourceInfo) => {
      const success = await startAnalysisAndGetCategories(
        textToSubmit,
        locationToSubmit,
        sourceInfo
      );
      if (success) {
        // Remove the automatic navigation for skip flow
        console.log(
          "[AIMatchmakingPage] Analysis complete. Showing career path options."
        );
        setHasSearchedJobs(false); // Reset job search state
      } else {
        console.error(
          "[AIMatchmakingPage] Analysis initiation failed. Error should be in context's categoryError."
        );
      }
    },
    [startAnalysisAndGetCategories]
  );

  const handleGenerateResume = useCallback(async () => {
    if (!selectedCategory) {
      alert("Please select a job category first.");
      return;
    }
    const success = await generateMasterResumeAction();
    if (success) {
      navigate("/dashboard/resume-builder");
    } else {
      console.error("[AIMatchmakingPage] Master resume generation failed.");
    }
  }, [selectedCategory, generateMasterResumeAction, navigate]);

  // New handler for searching jobs based on selected category
  const handleSearchJobs = useCallback(async () => {
    if (!selectedCategory) {
      alert("Please select a job category first.");
      return;
    }

    setHasSearchedJobs(true);

    // If searchJobsForCategory is available in context, use it
    if (searchJobsForCategory) {
      const success = await searchJobsForCategory(
        selectedCategory,
        localLocation
      );
      if (success && aiMatchedJobs.length > 0) {
        // Optional: Navigate immediately after successful search
        // navigate("/dashboard/job-listing");
      }
    } else {
      // Fallback: You might need to implement this function in your context
      console.error(
        "[AIMatchmakingPage] searchJobsForCategory not available in context"
      );
    }
  }, [selectedCategory, localLocation, searchJobsForCategory, aiMatchedJobs]);

  const handleReset = useCallback(() => {
    resetWorkflow();
    setHasSearchedJobs(false);
  }, [resetWorkflow]);

  const canUseSavedProfile = !!(
    currentUser &&
    currentUser.fullName &&
    currentUser.isProfileComplete
  );

  const savedProfileTextForAI = currentUser?.profileData?.profileText || "";

  useEffect(() => {
    // This effect is for debugging purposes
    console.log(
      "--- AIMatchmakingPage currentUser Debug (Button Enabling Logic) ---"
    );
    if (currentUser) {
      console.log("currentUser object:", currentUser);
      console.log(
        "currentUser.isProfileComplete (from AuthContext via Backend):",
        currentUser.isProfileComplete
      );
      console.log("currentUser.fullName:", currentUser.fullName);
      console.log(
        "savedProfileTextForAI (passed to ProfileInput):",
        savedProfileTextForAI
          ? savedProfileTextForAI.substring(0, 100) + "..."
          : "empty"
      );
      console.log(
        "Resulting canUseSavedProfile (for enabling 'Use My Saved Profile'):",
        canUseSavedProfile
      );
    } else {
      console.log("currentUser is null or undefined.");
    }
    console.log(
      "--------------------------------------------------------------------"
    );
  }, [currentUser, canUseSavedProfile, savedProfileTextForAI]);

  return (
    <div className="ai-matchmaking-page">
      {/* Enhanced container with improved styling */}
      <div
        className="ai-matchmaking-container"
        style={{
          minHeight: "100vh",
          paddingLeft: "2rem",
          paddingRight: "2rem",
        }}
      >
        <div
          className="content-wrapper"
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: "2rem",
          }}
        >
          {/* Main Content Area */}
          <div
            className="main-content"
            style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
          >
            {/* Profile Input Section */}
            {!aiTaskId &&
              jobCategories.length === 0 &&
              !isLoadingCategories && (
                <div className="profile-input-section">
                  <ProfileInput
                    location={localLocation}
                    locations={canadianProvincesAndTerritories}
                    onLocationChange={setLocalLocation}
                    onSubmit={handleAnalyze}
                    isLoading={isLoadingCategories}
                    error={categoryError}
                    className="widget-card"
                    isProfileComplete={canUseSavedProfile}
                    savedProfileText={savedProfileTextForAI}
                  />
                </div>
              )}

            {/* Enhanced Loading State */}
            {isLoadingCategories && (
              <div
                className="loading-section"
                style={{
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "24px",
                  padding: "3rem",
                  textAlign: "center",
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                <div
                  className="loading-spinner"
                  style={{
                    width: "60px",
                    height: "60px",
                    border: "4px solid #e2e8f0",
                    borderTop: "4px solid rgb(229, 88, 18)",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 1.5rem",
                  }}
                />
                <h3
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "600",
                    color: "#2d3748",
                    marginBottom: "0.5rem",
                  }}
                >
                  Analyzing Your Profile
                </h3>
                <p
                  style={{
                    fontSize: "1.1rem",
                    color: "#718096",
                    marginBottom: "0.5rem",
                  }}
                >
                  Our AI is searching for relevant career paths...
                </p>
                <p
                  style={{
                    fontSize: "0.9rem",
                    color: "#a0aec0",
                  }}
                >
                  This may take a moment while we analyze your skills and
                  experience.
                </p>
              </div>
            )}

            {/* Job Category Selector - Modified to show job search button */}
            {!isLoadingCategories &&
              jobCategories.length > 0 &&
              !hasSearchedJobs && (
                <div className="job-category-section">
                  <JobCategorySelector
                    categories={jobCategories}
                    selectedCategory={selectedCategory}
                    onSelectCategory={setSelectedCategory}
                    onGenerateResume={handleGenerateResume}
                    onSearchJobs={handleSearchJobs}
                    isLoading={isLoadingMasterResume}
                    isLoadingJobs={isLoadingAiJobs}
                    error={masterResumeError}
                    className="widget-card"
                  />
                </div>
              )}

            {/* Enhanced No Categories Message */}
            {!isLoadingCategories &&
              categoryError === null &&
              jobCategories.length === 0 &&
              aiTaskId &&
              !flowShouldSkipResumeBuilder && (
                <div
                  className="no-categories-section"
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "24px",
                    padding: "3rem",
                    textAlign: "center",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "3rem",
                      marginBottom: "1rem",
                    }}
                  >
                    🔍
                  </div>
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: "#2d3748",
                      marginBottom: "1rem",
                    }}
                  >
                    Analysis Complete
                  </h3>
                  <p
                    style={{
                      fontSize: "1.1rem",
                      color: "#718096",
                      marginBottom: "0.5rem",
                    }}
                  >
                    No specific job categories identified for resume generation.
                  </p>
                  <p
                    style={{
                      fontSize: "0.9rem",
                      color: "#a0aec0",
                    }}
                  >
                    However, we will still try to find job matches based on your
                    input.
                  </p>
                </div>
              )}

            {/* Enhanced Job Search Loading - Only show after button click */}
            {hasSearchedJobs &&
              (isLoadingAiJobs || pollingForJobs) &&
              (!aiMatchedJobs || aiMatchedJobs.length === 0) && (
                <div
                  className="job-search-loading"
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "24px",
                    padding: "3rem",
                    textAlign: "center",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <div
                    className="loading-spinner"
                    style={{
                      width: "60px",
                      height: "60px",
                      border: "4px solid #e2e8f0",
                      borderTop: "4px solid rgb(7, 50, 20)",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      margin: "0 auto 1.5rem",
                    }}
                  />
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: "#2d3748",
                      marginBottom: "1rem",
                    }}
                  >
                    Searching for Opportunities
                  </h3>
                  <p
                    style={{
                      fontSize: "1.1rem",
                      color: "#718096",
                    }}
                  >
                    Finding relevant job opportunities for{" "}
                    {selectedCategory?.title || "your selected career path"}...
                  </p>
                </div>
              )}

            {/* Enhanced Error Message */}
            {hasSearchedJobs &&
              aiJobsError &&
              !isLoadingAiJobs &&
              !pollingForJobs && (
                <div
                  className="error-section"
                  style={{
                    background: "rgba(254, 242, 242, 0.95)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "24px",
                    padding: "3rem",
                    textAlign: "center",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                    border: "2px solid rgb(229, 88, 18)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "3rem",
                      marginBottom: "1rem",
                    }}
                  >
                    ⚠️
                  </div>
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: "rgb(194, 65, 12)",
                      marginBottom: "1rem",
                    }}
                  >
                    Error Finding Jobs
                  </h3>
                  <p
                    style={{
                      fontSize: "1.1rem",
                      color: "rgb(194, 65, 12)",
                    }}
                  >
                    {aiJobsError}
                  </p>
                </div>
              )}

            {/* Enhanced Success - Jobs Found */}
            {hasSearchedJobs &&
              !isLoadingAiJobs &&
              !pollingForJobs &&
              aiMatchedJobs.length > 0 && (
                <div
                  className="jobs-found-section"
                  style={{
                    background: "rgba(240, 253, 244, 0.95)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "24px",
                    padding: "3rem",
                    textAlign: "center",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                    border: "2px solid rgb(7, 50, 20)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "3rem",
                      marginBottom: "1rem",
                    }}
                  >
                    🎉
                  </div>
                  <h3
                    style={{
                      fontSize: "1.8rem",
                      fontWeight: "700",
                      color: "rgb(7, 50, 20)",
                      marginBottom: "1rem",
                    }}
                  >
                    AI Matched Jobs Found!
                  </h3>
                  <p
                    style={{
                      fontSize: "1.2rem",
                      color: "rgb(5, 35, 14)",
                      marginBottom: "2rem",
                    }}
                  >
                    Found {aiMatchedJobs.length} potential matches for{" "}
                    {selectedCategory?.title || "your selected career path"}.
                  </p>
                  <button
                    onClick={() => navigate("/dashboard/job-listing")}
                    style={{
                      background:
                        "linear-gradient(135deg, rgb(7, 50, 20), rgb(5, 35, 14))",
                      color: "white",
                      border: "none",
                      padding: "1.25rem 3rem",
                      borderRadius: "50px",
                      fontSize: "1.1rem",
                      fontWeight: "700",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 10px 30px rgba(7, 50, 20, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-3px)";
                      e.target.style.boxShadow =
                        "0 15px 40px rgba(7, 50, 20, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 10px 30px rgba(7, 50, 20, 0.3)";
                    }}
                  >
                    View Matched Jobs →
                  </button>
                </div>
              )}

            {/* Enhanced No Jobs Found */}
            {hasSearchedJobs &&
              !isLoadingAiJobs &&
              !pollingForJobs &&
              aiMatchedJobs.length === 0 &&
              !aiJobsError && (
                <div
                  className="no-jobs-section"
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "24px",
                    padding: "3rem",
                    textAlign: "center",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "3rem",
                      marginBottom: "1rem",
                    }}
                  >
                    🔍
                  </div>
                  <h3
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: "600",
                      color: "#2d3748",
                      marginBottom: "1rem",
                    }}
                  >
                    No Matches Found
                  </h3>
                  <p
                    style={{
                      fontSize: "1.1rem",
                      color: "#718096",
                    }}
                  >
                    No specific job matches found for{" "}
                    {selectedCategory?.title || "this career path"}.
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
}

export default AIMatchmakingPage;
